//! Pointer interaction for Excel-native charts (floating objects rendered by
//! ChartsLayer). Click selects a chart; dragging a selected chart moves it;
//! dragging its edges/corners resizes it. Escape cancels an in-flight
//! drag/resize; changes commit through `quadraticCore.setChartRect` on
//! pointer up.

import { hasPermissionToEditFile } from '@/app/actions';
import { ContextMenuType } from '@/app/atoms/contextMenuAtom';
import { setMobilePane } from '@/app/atoms/mobileStateAtom';
import { events } from '@/app/events/events';
import { sheets } from '@/app/grid/controller/Sheets';
import type { ChartHit } from '@/app/gridGL/charts/ChartsLayer';
import { DOUBLE_CLICK_TIME, MINIMUM_MOVE_POSITION } from '@/app/gridGL/interaction/pointer/pointerUtils';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import * as gridSettings from '@/app/gridGL/pixiApp/gridSettings';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { isMobileMode } from '@/app/helpers/isMobile';
import { quadraticCore } from '@/app/web-workers/quadraticCore/quadraticCore';
import { isMac } from '@/shared/utils/isMac';
import { Rectangle, type FederatedPointerEvent, type Point } from 'pixi.js';

const MIN_CHART_SIZE = 40;

interface Dragging {
  chartId: string;
  handle: ChartHit['handle'];
  start: Point;
  originalRect: Rectangle;
  currentRect: Rectangle;
  moved: boolean;
}

export class PointerCharts {
  cursor: string | undefined;

  private dragging?: Dragging;

  get isDragging(): boolean {
    return !!this.dragging;
  }

  // whether a data-point hover tooltip is currently showing
  private hoverActive = false;

  // live-reflow preview: at most one layout request in flight; a trailing
  // request fires when the rect changed while waiting
  private previewInFlight = false;
  private previewQueued = false;

  private requestPreview() {
    const drag = this.dragging;
    if (!drag || drag.handle === 'move') return;
    if (this.previewInFlight) {
      this.previewQueued = true;
      return;
    }
    this.previewInFlight = true;
    const rect = drag.currentRect.clone();
    quadraticCore.getChartPreview(sheets.current, drag.chartId, rect.width, rect.height).then((preview) => {
      this.previewInFlight = false;
      // the drag may have ended or moved to another chart while waiting
      if (this.dragging?.chartId !== drag.chartId) return;
      if (preview) {
        content.chartsLayer.setTransientOps(drag.chartId, rect, preview);
      }
      if (this.previewQueued) {
        this.previewQueued = false;
        this.requestPreview();
      }
    });
  }

  private cursorFor(handle: ChartHit['handle']): string {
    switch (handle) {
      case 'move':
        return 'default';
      case 'n':
      case 's':
        return 'ns-resize';
      case 'e':
      case 'w':
        return 'ew-resize';
      case 'ne':
      case 'sw':
        return 'nesw-resize';
      case 'nw':
      case 'se':
        return 'nwse-resize';
    }
  }

  private dragRect(drag: Dragging, point: Point): Rectangle {
    const dx = point.x - drag.start.x;
    const dy = point.y - drag.start.y;
    const o = drag.originalRect;

    if (drag.handle === 'move') {
      return new Rectangle(Math.max(0, o.x + dx), Math.max(0, o.y + dy), o.width, o.height);
    }

    let { x, y } = o;
    let { width, height } = o;
    if (drag.handle.includes('w')) {
      x = Math.min(o.x + dx, o.x + o.width - MIN_CHART_SIZE);
      width = o.width - (x - o.x);
    }
    if (drag.handle.includes('e')) {
      width = Math.max(MIN_CHART_SIZE, o.width + dx);
    }
    if (drag.handle.includes('n')) {
      y = Math.min(o.y + dy, o.y + o.height - MIN_CHART_SIZE);
      height = o.height - (y - o.y);
    }
    if (drag.handle.includes('s')) {
      height = Math.max(MIN_CHART_SIZE, o.height + dy);
    }
    return new Rectangle(Math.max(0, x), Math.max(0, y), width, height);
  }

  // double-click tracking: opens the chart editor when the pointer stays
  // roughly in place between two quick clicks on the same chart
  private doubleClickTimeout?: number;
  private lastClickedChartId?: string;
  private lastClickedPoint?: Point;

  private clearDoubleClick() {
    if (this.doubleClickTimeout) {
      window.clearTimeout(this.doubleClickTimeout);
      this.doubleClickTimeout = undefined;
    }
    this.lastClickedChartId = undefined;
    this.lastClickedPoint = undefined;
  }

  pointerDown(point: Point, event?: FederatedPointerEvent): boolean {
    const hit = content.chartsLayer.hitTest(point);
    if (!hit) {
      // clicking away deselects
      if (content.chartsLayer.selected) {
        content.chartsLayer.select(undefined);
      }
      this.clearDoubleClick();
      return false;
    }

    this.clearHover();
    const wasSelected = content.chartsLayer.selected?.id === hit.chart.id;
    content.chartsLayer.select(hit.chart.id);

    // right-click opens the chart context menu
    const isRightClick = !!event && (event.button === 2 || (isMac && event.button === 0 && event.ctrlKey));
    if (isRightClick) {
      this.clearDoubleClick();
      events.emit('contextMenu', {
        type: ContextMenuType.Chart,
        world: point.clone(),
      });
      return true;
    }

    // clicking a data element highlights it on the chart and highlights the
    // cells its data comes from (like Excel's source-range outlines)
    if (hit.handle === 'move') {
      const region = content.chartsLayer.hitTestRegion(point);
      if (region) {
        content.chartsLayer.highlightRegion(region);
      } else {
        content.chartsLayer.clearRegionHighlight();
      }
    }

    if (!hasPermissionToEditFile(gridSettings.getPermissions())) {
      return true;
    }

    // double-click opens the chart editor
    const movedTooFar =
      !this.lastClickedPoint ||
      Math.abs(this.lastClickedPoint.x - point.x) + Math.abs(this.lastClickedPoint.y - point.y) >
        MINIMUM_MOVE_POSITION / pixiApp.viewport.scale.x;
    if (!movedTooFar && this.doubleClickTimeout && this.lastClickedChartId === hit.chart.id) {
      this.clearDoubleClick();
      gridSettings.updateEditorInteractionState((prev) => ({
        ...prev,
        showChartPanel: hit.chart.id,
      }));
      // the chart panel has no fixed mount on mobile; open its pane
      if (isMobileMode()) {
        setMobilePane('charts');
      }
      return true;
    }
    this.clearDoubleClick();
    this.lastClickedChartId = hit.chart.id;
    this.lastClickedPoint = point.clone();
    this.doubleClickTimeout = window.setTimeout(() => this.clearDoubleClick(), DOUBLE_CLICK_TIME);

    // moving requires the chart to already be selected (select-then-move,
    // matching image/table behavior); resize handles only hit when selected
    if (hit.handle !== 'move' || wasSelected) {
      this.dragging = {
        chartId: hit.chart.id,
        handle: hit.handle,
        start: point.clone(),
        originalRect: new Rectangle(hit.chart.x, hit.chart.y, hit.chart.w, hit.chart.h),
        currentRect: new Rectangle(hit.chart.x, hit.chart.y, hit.chart.w, hit.chart.h),
        moved: false,
      };
      // stop the viewport's drag-to-pan (always active on touch) from
      // fighting the chart move/resize gesture
      pixiApp.viewport.pauseDrag();
    }
    return true;
  }

  /** Shows the Excel-style tooltip for the data point under the pointer. */
  private updateHover(world: Point) {
    const region = content.chartsLayer.hitTestRegion(world);
    if (region) {
      this.hoverActive = true;
      // anchor the tooltip to the pointer so it follows the hovered element
      events.emit('hoverTooltip', new Rectangle(world.x, world.y, 0, 0), region.title, region.value);
    } else {
      this.clearHover();
    }
  }

  private clearHover() {
    if (!this.hoverActive) return;
    this.hoverActive = false;
    events.emit('hoverTooltip');
  }

  pointerMove(point: Point): boolean {
    if (this.dragging) {
      this.clearHover();
      const rect = this.dragRect(this.dragging, point);
      this.dragging.currentRect = rect;
      this.dragging.moved =
        this.dragging.moved ||
        rect.x !== this.dragging.originalRect.x ||
        rect.y !== this.dragging.originalRect.y ||
        rect.width !== this.dragging.originalRect.width ||
        rect.height !== this.dragging.originalRect.height;
      content.chartsLayer.setTransientRect(this.dragging.chartId, rect);
      // resize: ask core for a reflowed layout at the new size (throttled to
      // one request in flight; the stretch above fills the sub-frame gap)
      this.requestPreview();
      this.cursor = this.dragging.handle === 'move' ? 'grabbing' : this.cursorFor(this.dragging.handle);
      return true;
    }

    const hit = content.chartsLayer.hitTest(point);
    if (hit) {
      this.updateHover(point);
      this.cursor = this.cursorFor(hit.handle);
      return hit.handle !== 'move';
    }
    this.clearHover();
    this.cursor = undefined;
    return false;
  }

  pointerUp(): boolean {
    if (!this.dragging) return false;
    const drag = this.dragging;
    this.dragging = undefined;
    this.previewQueued = false;
    pixiApp.viewport.resumeDrag();

    if (drag.moved) {
      const rect = drag.currentRect;
      quadraticCore.setChartRect(sheets.current, drag.chartId, rect.x, rect.y, rect.width, rect.height);
    }
    return true;
  }

  /**
   * Cancels an in-progress move/resize drag (restoring the chart) without
   * touching the selection. Used when a gesture is interrupted (window blur,
   * cursor leaving the canvas) — deselecting there would break flows that
   * rely on the selection surviving, like the context menu.
   */
  cancelGesture(): boolean {
    this.clearHover();
    if (this.dragging) {
      content.chartsLayer.restore(this.dragging.chartId);
      this.dragging = undefined;
      this.previewQueued = false;
      pixiApp.viewport.resumeDrag();
      return true;
    }
    return false;
  }

  handleEscape(): boolean {
    if (this.cancelGesture()) {
      return true;
    }
    // first Escape clears a highlighted element, the next deselects the
    // chart (matching Excel)
    if (content.chartsLayer.hasRegionHighlight) {
      content.chartsLayer.clearRegionHighlight();
      return true;
    }
    if (content.chartsLayer.selected) {
      content.chartsLayer.select(undefined);
      return true;
    }
    return false;
  }

  /** Nudges the selected chart by pixels (arrow keys). */
  handleNudge(dx: number, dy: number): boolean {
    const chart = content.chartsLayer.selected;
    if (!chart) return false;
    if (!hasPermissionToEditFile(gridSettings.getPermissions())) return false;
    const x = Math.max(0, chart.x + dx);
    const y = Math.max(0, chart.y + dy);
    quadraticCore.setChartRect(sheets.current, chart.id, x, y, chart.w, chart.h);
    return true;
  }

  /** Deletes the selected chart. Returns true when a chart was deleted. */
  handleDelete(): boolean {
    const selected = content.chartsLayer.selected;
    if (!selected) return false;
    if (!hasPermissionToEditFile(gridSettings.getPermissions())) return false;
    quadraticCore.removeChart(sheets.current, selected.id, false);
    content.chartsLayer.select(undefined);
    events.emit('setDirty', { cursor: true });
    return true;
  }
}
